import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { shareReplay, takeUntil, take } from "rxjs/operators";
import { SelectOption } from "src/app/shared/models/select-option.interface";
import { CountriesService } from "@shared/services/countries/countries.service";
import { CurrencyService } from "@shared/services/currency/currency.service";
import { Store, select } from "@ngrx/store";
import { AppState } from "src/app/store/models/state.model";
import { getProviderState, getSelectedTPPServiceState } from "../../../../store";
import { TppCommonServiceService } from "../../_services/common/tpp-common-service.service";
import { BankAccountFieldsComponent } from "@shared/components/bank-account-fields/bank-account-fields.component";
import { TppAccount } from "src/app/shared/models/tpp-account.interface";
import { BankAccountsService } from "@shared/services/bank-accounts/bank-accounts.service";
import { TppServiceManagementService } from "../../_services/management/tpp-service-management.service";
import { ToastService } from "@shared/services/toast/toast.service";
import { TppServiceBeneficiaryDTO } from "src/app/shared/models/tpp-service.interface";
import { ServiceProviderService } from "@shared/services/service-provider/service-provider.service";
import { ProviderType } from "@shared/models/service-provider-types.interface";

@Component({
	selector: "app-tpp-add-beneficiary",
	templateUrl: "./tpp-add-beneficiary.component.html",
	styleUrls: ["./tpp-add-beneficiary.component.scss"]
})
export class TppAddBeneficiaryComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() selectedCountry: string | undefined;
	@Input() canEdit = true;

	@Output() newBeneficiary: EventEmitter<TppServiceBeneficiaryDTO> = new EventEmitter<TppServiceBeneficiaryDTO>();

	//ViewChild
	@ViewChild(BankAccountFieldsComponent) bankAccountFieldsComponent: BankAccountFieldsComponent | undefined;
	bankAccountFields!: BankAccountFieldsComponent;

	newBeneficiaryForm!: FormGroup;
	countries$!: Observable<SelectOption[]>;
	currencies$: Observable<SelectOption[]> = new Observable<SelectOption[]>();
	formIsPristine = true;
	formIsComplete = false;
	formIsValid = false;
	formIsUpdated = false;
	serviceId = "";

	public bankAccountValues: Record<string, string | number | boolean> | undefined;
	selectedCountryName!: SelectOption | string;
	accountBeneficiary!: TppAccount;
	bankName = "";
	btText = "SAVE";

	destroy$ = new Subject<void>();
	skipValidation = false;

	private formBuilder = inject(FormBuilder);
	private countriesService = inject(CountriesService);
	private currencyService = inject(CurrencyService);
	private tppCommonService = inject(TppCommonServiceService);
	private store = inject(Store<AppState>);
	private bankAccountsService = inject(BankAccountsService);
	private tppServiceManagementService = inject(TppServiceManagementService);
	private toastService = inject(ToastService);
	private cdr = inject(ChangeDetectorRef);
	private serviceProvider = inject(ServiceProviderService);

	ngOnInit(): void {
		this.countries$ = this.countriesService.getCountryOptions().pipe(shareReplay(1));
		this.currencies$ = this.currencyService.getCurrencyOptions().pipe(shareReplay(1));

		this.store.pipe(takeUntil(this.destroy$), select(getSelectedTPPServiceState)).subscribe(state => {
			this.serviceId = state.selectedTPPService.tppService!.id;
		});

		this.countries$.pipe(take(1)).subscribe(x => {
			this.selectedCountryName = x.find(country => country.value === this.selectedCountry)!;
			this.initializeForm();
		});

		this.store.pipe(takeUntil(this.destroy$), select(getProviderState)).subscribe(state => {
			if (state && state.provider && state.provider !== "all" && state.provider !== "") {
				this.serviceProvider
					.getProviderType(state.provider)
					.pipe(takeUntil(this.destroy$))
					.subscribe(type => {
						this.skipValidation = type !== ProviderType.API;
					});
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.bankAccountFields = this.bankAccountFieldsComponent!;
		this.cdr.detectChanges();
	}

	initializeForm(): void {
		this.newBeneficiaryForm = this.formBuilder.group({
			account_name: ["", Validators.required],
			country: ["", Validators.required],
			currency: ["", Validators.required]
		});

		if (!this.canEdit) {
			this.newBeneficiaryForm.disable();
		}

		this.newBeneficiaryForm.patchValue({
			country: this.selectedCountryName
		});

		this.editBeneficiaryAccount();

		this.bankAccountsService.formUpdated$.subscribe(() => {
			if (this.bankAccountFieldsComponent)
				this.formIsPristine = this.bankAccountFieldsComponent.bankAccountForm.pristine;
		});

		this.bankAccountsService.formUpdatedValidity$.subscribe(response => {
			if (response) this.formIsComplete = true;
		});

		this.newBeneficiaryForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.formIsPristine = this.newBeneficiaryForm.pristine;
				if (this.newBeneficiaryForm.valid) {
					this.formIsValid = true;
				}
			}
		});
	}

	/**
	 * To check whether the user updated any bank fields
	 * @param event: value that checks if form has changes
	 */
	isTppServiceBankFieldsUpdated(event: boolean): void {
		this.formIsUpdated = !event;
	}

	editBeneficiaryAccount(): void {
		if (Object.keys(this.tppServiceManagementService.beneficiaryAccount).length > 0) {
			this.accountBeneficiary = this.tppServiceManagementService.beneficiaryAccount;
			this.newBeneficiaryForm.patchValue({
				account_name: this.accountBeneficiary.name,
				currency: this.accountBeneficiary.currency
			});
			this.bankAccountValues = this.generateRecordsForBankAccount(this.accountBeneficiary.bankAccount.fields);
			this.selectedCountryName = this.accountBeneficiary.bankAccount.country;
			this.bankName = this.accountBeneficiary.bankAccount.bankName;
			this.btText = "UPDATE";
		}
	}

	generateRecordsForBankAccount(fields: { key: string; value: string }[]): Record<string, string | number | boolean> {
		const bankAccountRecords: Record<string, string | number | boolean> = {};
		fields.forEach((item: { key: string; value: string }) => (bankAccountRecords[item.key] = item.value));
		return bankAccountRecords;
	}

	backFromModal() {
		this.tppServiceManagementService.setBeneficairyAccountState(false);
	}

	saveBeneficiary(): void {
		const rawFormData: { account_name: string; country: SelectOption; currency: string } =
			this.newBeneficiaryForm.getRawValue();

		if (this.btText === "SAVE")
			this.tppCommonService
				.saveNewBeneficiary(
					rawFormData,
					this.bankAccountFieldsComponent!.bankAccountForm.controls,
					this.serviceId,
					this.skipValidation
				)
				.subscribe({
					next: data => {
						this.newBeneficiary.emit(data);
						this.toastService.showSuccess("Account created successfully");
						this.tppServiceManagementService.setBeneficairyAccountState(false);
					},
					error: error => {
						this.toastService.showError(error.error[0].message);
					}
				});

		if (this.btText === "UPDATE")
			this.tppCommonService
				.updateNewBeneficairy(
					rawFormData,
					this.bankAccountFieldsComponent!.bankAccountForm.controls,
					this.accountBeneficiary,
					this.skipValidation
				)
				.subscribe({
					next: data => {
						this.newBeneficiary.emit(data);
						this.toastService.showSuccess("Account updated successfully");
						this.tppServiceManagementService.setBeneficairyAccountState(false);
					},
					error: error => {
						this.toastService.showError(error.error[0].message);
					}
				});
	}
}
